import { Controller } from "stimulus"
import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select';

export default class extends Controller {
  static targets = ['input', 'label']

  static values = {
    options: Object
  }

  initialize() {
    this.tomSelect = new TomSelect(this.inputTarget, this.selectOptions);
  }

  connect() {
    if (this.hasLabelTarget) {    
      this.labelTarget.addEventListener('click', (e) => {
        this.tomSelect.focus()
      })
    }

    if (this.inputTarget.attributes['required']) {
      this.observer = new MutationObserver((mutations) => this.processMutations(mutations));
      this.observer.observe(this.inputTarget, { attributes: true, attributeFilter: ["error"] });
    }
  }

  disconnect() {
    if (this.hasLabelTarget) {
      this.labelTarget.removeEventListener('click')
    }

    if (this.inputTarget.attributes['required']) {
      this.observer.disconnect();
    }
  }

  get templateOptionString() {
    const template = this.element.querySelector("#option-template")
    if(template) {
      return template.innerHTML.toString()
    }
  }

  get templateItemString() {
    const template = this.element.querySelector("#item-template")
    if(template) {
      return template.innerHTML.toString()
    }
  }

  createTemplateFunction(str) {
    return new Function('data', 'escape', `return \`${str}\`;`)
  }

  get render() {
    let renders = {};
    if(this.templateOptionString) {
      renders['option'] = this.createTemplateFunction(this.templateOptionString)
    }

    if(this.templateItemString) {
      renders['item'] = this.createTemplateFunction(this.templateItemString)
    }
    return renders;
  }

  get searchParams() {
    if (this.optionsValue?.remoteUrl?.indexOf("?") > -1) {
      const idx = this.optionsValue.remoteUrl.indexOf("?")
      const queryString = this.optionsValue.remoteUrl.substring(idx)
      const sp = new URLSearchParams(queryString)

      return Object.fromEntries(sp);
    } else {
      return {}
    }
  }

  get cleanedRemoteUrl() {
    if (this.optionsValue?.remoteUrl) {
      if (this.optionsValue.remoteUrl.indexOf("?") > -1) {
        return this.optionsValue.remoteUrl.substr(0, this.optionsValue.remoteUrl.indexOf("?"))
      }

      return this.optionsValue.remoteUrl
    }

    return undefined;
  }

  get remoteOptions() {
    if(this.optionsValue?.remoteUrl) {
      //Fields are easily overrideable by passing them in to the component
      return {
        load: this.load,
        valueField: 'value',
        labelField: 'value',
        searchField: 'value'
      }
    } else {
      return {}
    }
  }

  get defaultOptions() {
    return { 
      render: this.render,
      onChange: this.onChange,
      shouldLoad: this.shouldLoad,
      onOptionAdd: this.onOptionAdd,
      onFocus: this.onFocusChange,
      onBlur: this.onBlurChange,
      onInitialize: this.onInitialize,
      maxItems: 1,
      minimumQueryLength: 3,
      closeOnSelect: true,
      placeholder: 'Select a value',
      queryOptions: {
        method: 'POST',
      }
    }
  }

  get selectOptions() {
    return Object.assign(
      this.defaultOptions,
      this.remoteOptions,
      this.optionsValue
    )
  }

  processMutations = (mutationsList) => {
    mutationsList.forEach(mutation => {
      if (mutation.type === 'attributes' && mutation.attributeName == 'error') {
        if (mutation?.target?.attributes['error']) {
          this.tomSelect?.control?.setAttribute('error', true)
        } else {
          this.tomSelect?.control?.removeAttribute('error')
        }
      }
    })
  }

  onChange = (value) => {
    this.dispatchEvent('sa-multiselect-change', value, this.inputTarget.value)
  }

  shouldLoad = (query) => {
    return query.length >= this.selectOptions.minimumQueryLength
  }

  onFocusChange = () => {
    this.tomSelect?.control?.setAttribute('active', true)
  }

  onBlurChange = () => {
    this.tomSelect?.control?.removeAttribute('active')
  }

  onInitialize = () => {
    let _value = this.inputTarget.attributes['value']

    // only preload for remoteUrl selects -- otherwise HTML will do it's thing
    // if it's a standard select with pre-defined options given
    if (this.selectOptions.remoteUrl && _value) {
      this.preload(_value?.value)
    }
  }

  load = (query, callback) => {
    var search = Object.assign({}, this.searchParams, {query: encodeURIComponent(query)})
    var sp = new URLSearchParams(search).toString()

    var url = `${this.cleanedRemoteUrl}?${sp}`;
    fetch(url, { method: this.selectOptions.queryOptions.method })
      .then(response => response.json())
      .then(q => callback(q.results))
      .catch(() => callback())
  }

  onOptionAdd = (value, data) => {
    if (!this.preloading) {
      this.dispatchEvent('sa-multiselect-option-added', value, value)
    }
  }

  preload = (value) => {
    if (!value) {
      return
    }

    const self = this;
    self.preloading = true

    var search = Object.assign({}, self.searchParams, {ids: encodeURIComponent(value)})
    var sp = new URLSearchParams(search).toString()

    var url = `${self.cleanedRemoteUrl}?${sp}`;
    fetch(url, { method: self.selectOptions.queryOptions.method })
      .then(response => response.json())
      .then(q => {
        self.tomSelect.addOptions(q.results, true)
        self.tomSelect.setValue(q.results.map(r => r.value), true)
      })
      .catch((e) => console.error('Error preloading selections', self, self.element, e))
      .finally(() => {
        self.preloading = false
      })
  }

  dispatchEvent = (eventName, value, text) => {
    this.element.dispatchEvent(
      new CustomEvent(
        eventName,
        { 
          bubbles: true, 
          detail: { value: value, text: text }
        }
      )
    )
  }
}
